import { useMemo } from 'react';
import { FaDownload, FaShieldAlt, FaCode, FaBrain, FaAward, FaProjectDiagram, FaGamepad } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isModern = theme === 'modern';

  const uniquePoints = useMemo(() => {
    const points = t('about.card.uniqueList', { returnObjects: true });
    return Array.isArray(points) ? (points as string[]) : [];
  }, [t]);

  const stats = [
    { icon: <FaProjectDiagram className="w-6 h-6" />, number: '20+', label: t('about.stats.projects') },
    { icon: <FaShieldAlt className="w-6 h-6" />,     number: '06+', label: t('about.stats.years') },
    { icon: <FaCode className="w-6 h-6" />,           number: '20+', label: t('about.stats.technologies') },
    { icon: <FaAward className="w-6 h-6" />,          number: '08+', label: t('about.stats.certifications') }
  ];

  // Ordered to match the site-wide flow: AI → Full-Stack → Game Dev → Security
  const highlights = [
    { icon: <FaBrain className="w-8 h-8" />,      title: t('about.highlights.ai.title'),            description: t('about.highlights.ai.description') },
    { icon: <FaCode className="w-8 h-8" />,       title: t('about.highlights.fullstack.title'),     description: t('about.highlights.fullstack.description') },
    { icon: <FaGamepad className="w-8 h-8" />,    title: t('about.highlights.gamedev.title'),       description: t('about.highlights.gamedev.description') },
    { icon: <FaShieldAlt className="w-8 h-8" />,  title: t('about.highlights.cybersecurity.title'), description: t('about.highlights.cybersecurity.description') }
  ];

  const sectionStyles = isModern
    ? 'modern-section min-h-screen relative py-20 overflow-hidden'
    : 'min-h-screen relative py-20 overflow-hidden';

  const downloadButtonStyles = isModern
    ? 'modern-button-primary inline-flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 w-full sm:w-auto'
    : 'inline-flex items-center justify-center gap-2 px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-yellow-400 border-4 border-black rounded-lg text-black font-black text-sm sm:text-base lg:text-lg hover:-translate-y-3 hover:-translate-x-3 hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] hover:rotate-2 transition-all duration-300 transform-gpu w-full sm:w-auto';

  const ctaButtonStyles = (isPrimary: boolean) => {
    if (isModern) {
      return isPrimary
        ? 'modern-button-primary px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 w-full sm:w-auto'
        : 'modern-button-secondary px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 w-full sm:w-auto';
    }
    return isPrimary
      ? 'px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-purple-400 border-4 border-black rounded-lg text-black font-black text-sm sm:text-base lg:text-lg hover:-translate-y-3 hover:-translate-x-3 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:rotate-1 transition-all duration-300 transform-gpu w-full sm:w-auto'
      : 'px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-green-400 border-4 border-black rounded-lg text-black font-black text-sm sm:text-base lg:text-lg hover:-translate-y-3 hover:-translate-x-3 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:rotate-1 transition-all duration-300 transform-gpu w-full sm:w-auto';
  };

  return (
    <section id='about' className={sectionStyles}>
      {!isModern && (
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute bottom-40 left-20 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-10 w-48 h-48 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob animation-delay-4000"></div>
          <div className="absolute bottom-20 right-1/3 w-64 h-64 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob"></div>
        </div>
      )}

      <div className='max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 mt-16 sm:mt-20'>
        {isModern ? (
          /* Modern — HUD Operative Dossier */
          <article className="sf-dossier mb-12 sm:mb-16 lg:mb-20">
            <div className="sf-dossier__strip">
              <span className="sf-dossier__tag">DOSSIER</span>
              <span className="sf-dossier__sep">▌</span>
              <span className="sf-dossier__id">PROFILE_042 · OPS</span>
              <span className="sf-dossier__sep">▌</span>
              <span className="sf-dossier__live">
                <span className="sf-dossier__livedot"></span>
                LIVE
              </span>
            </div>

            <header className="sf-dossier__title-block">
              <h1 className="sf-dossier__h1">
                <span className="bg-clip-text text-transparent">{t('about.title')}</span>
              </h1>
              <p className="sf-dossier__subtitle">
                <span className="sf-bracket">[</span>
                <span>{t('about.subtitle')}</span>
                <span className="sf-bracket">]</span>
              </p>
            </header>

            <div className="sf-dossier__body">
              <section className="sf-dossier__col sf-dossier__col--side">
                <div className="sf-dossier__section-label">[ IDENTIFIERS ]</div>
                <dl className="sf-dossier__fields">
                  <div className="sf-dossier__field"><dt>NAME</dt><dd>Moti Rebuma</dd></div>
                  <div className="sf-dossier__field"><dt>ORIGIN</dt><dd>{t('about.card.location')}</dd></div>
                  <div className="sf-dossier__field"><dt>TRAINING</dt><dd>{t('about.card.education')}</dd></div>
                  <div className="sf-dossier__field"><dt>RECORD</dt><dd>{t('about.card.experience')}</dd></div>
                  <div className="sf-dossier__field"><dt>LANGUAGES</dt><dd>EN · AM · OR · JA</dd></div>
                </dl>
              </section>

              <section className="sf-dossier__col sf-dossier__col--main">
                <div className="sf-dossier__section-label">[ TRANSMISSION ]</div>
                <p className="sf-dossier__paragraph">
                  <span className="sf-dossier__prompt">&gt;_</span>
                  {t('about.card.introParagraph')}
                </p>
                <p className="sf-dossier__paragraph">
                  <span className="sf-dossier__prompt">&gt;_</span>
                  {t('about.card.strengthParagraph')}
                </p>
              </section>
            </div>

            <section className="sf-dossier__edge">
              <div className="sf-dossier__section-label">[ EDGE :: {t('about.card.uniqueTitle')} ]</div>
              <ul className="sf-dossier__edge-list">
                {uniquePoints.map((p, i) => (
                  <li key={`${p}-${i}`} className="sf-dossier__edge-item">
                    <span className="sf-dossier__edge-num">{String(i + 1).padStart(3, '0')}</span>
                    <span className="sf-dossier__edge-bar" aria-hidden="true"></span>
                    <span className="sf-dossier__edge-text">{p}</span>
                  </li>
                ))}
              </ul>
            </section>

            <div className="sf-dossier__actions">
              <a href="/Moti_Rebuma-Resume.pdf" download className={downloadButtonStyles}>
                <FaDownload className="w-5 h-5" />
                {t('about.downloadCv')}
              </a>
              <span className="sf-dossier__action-meta">
                FILE :: <strong>moti_rebuma-resume.pdf</strong> · ENC :: SHA-256
              </span>
            </div>
          </article>
        ) : (
          /* Neobrutalism — Punk passport file */
          <div className="mb-12 sm:mb-16 lg:mb-20 relative">

            <article className="nb-dossier">
              <div className="nb-dossier__strip">
                <span className="nb-dossier__strip-tag">FILE NO. 042 / OPS</span>
                <span className="nb-dossier__strip-id">CLASSIFIED :: PUBLIC</span>
              </div>

              <span className="nb-stamp nb-stamp--certified">★ CERTIFIED ★</span>
              <span className="nb-stamp nb-stamp--toprated">TOP RATED</span>

              <header className="nb-dossier__title-block">
                <h1 className="nb-dossier__h1">{t('about.title')}</h1>
                <p className="nb-dossier__subtitle">
                  &gt;&gt;&nbsp;{t('about.subtitle')}&nbsp;&lt;&lt;
                </p>
              </header>

              <div className="nb-dossier__body">
                <section>
                  <div className="nb-dossier__section-label">▼ PROFILE ▼</div>
                  <dl className="nb-dossier__fields">
                    <div className="nb-dossier__field"><dt>NAME</dt><dd>Moti Rebuma</dd></div>
                    <div className="nb-dossier__field"><dt>ORIGIN</dt><dd>{t('about.card.location')}</dd></div>
                    <div className="nb-dossier__field"><dt>TRAINING</dt><dd>{t('about.card.education')}</dd></div>
                    <div className="nb-dossier__field"><dt>RECORD</dt><dd>{t('about.card.experience')}</dd></div>
                    <div className="nb-dossier__field"><dt>LANGUAGES</dt><dd>EN · AM · OR · JA</dd></div>
                  </dl>
                </section>

                <section>
                  <div className="nb-dossier__section-label">► BIO ◄</div>
                  <div className="nb-bubble">
                    <p>{t('about.card.introParagraph')}</p>
                    <p>{t('about.card.strengthParagraph')}</p>
                  </div>
                </section>
              </div>

              <section className="mb-6">
                <div className="nb-dossier__section-label">★★★ {t('about.card.uniqueTitle').toUpperCase()} ★★★</div>
                <ul className="nb-edge-list">
                  {uniquePoints.map((p, i) => (
                    <li key={`${p}-${i}`} className={`nb-edge-item nb-edge-item--${i % 3}`}>
                      <span className="nb-edge-num">[{String(i + 1).padStart(2, '0')}]</span>
                      <span className="nb-edge-text">{p}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <div className="nb-dossier__actions">
                <a href="/Moti_Rebuma-Resume.pdf" download className={downloadButtonStyles}>
                  <FaDownload className="w-5 h-5" />
                  ↓ {t('about.downloadCv')}
                </a>
                <span className="nb-dossier__file-meta">► moti_rebuma-resume.pdf</span>
              </div>
            </article>
          </div>
        )}

        {/* ============== ACHIEVEMENTS / TELEMETRY ============== */}
        {isModern ? (
          <section className="mb-12 sm:mb-16 lg:mb-20">
            <div className="sf-block-header">
              <div className="sf-block-header__strip">
                <span className="sf-block-header__tag">TELEMETRY</span>
                <span className="sf-dossier__sep">▌</span>
                <span className="sf-block-header__id">MISSION_METRICS</span>
                <span className="sf-dossier__sep">▌</span>
                <span className="sf-block-header__live">
                  <span className="sf-dossier__livedot"></span>
                  ALL NOMINAL
                </span>
              </div>
              <h2 className="sf-block-header__h2">{t('about.achievementsTitle')}</h2>
              <p className="sf-block-header__sub">
                <span className="sf-bracket">[</span>{t('about.achievementsSubtitle')}<span className="sf-bracket">]</span>
              </p>
            </div>
            <div className="sf-metrics-grid">
              {stats.map((stat, index) => {
                const statusLabels = ['DELIVERED', 'UPTIME', 'MASTERED', 'VERIFIED'];
                return (
                  <article key={index} className="sf-metric modern-card">
                    <div className="sf-metric__head">
                      <span className="sf-metric__code">METRIC_{String(index + 1).padStart(2, '0')}</span>
                      <span className="sf-metric__pulse"></span>
                    </div>
                    <div className="sf-metric__icon">{stat.icon}</div>
                    <div className="sf-metric__value">{stat.number}</div>
                    <div className="sf-metric__label">{stat.label}</div>
                    <div className="sf-metric__bar"><span></span></div>
                    <div className="sf-metric__status">● {statusLabels[index]}</div>
                  </article>
                );
              })}
            </div>
          </section>
        ) : (
          <section className="mb-12 sm:mb-16 lg:mb-20">
            <div className="text-center mt-8 mb-8">
              <h2 className="nb-block-h2">★ {t('about.achievementsTitle').toUpperCase()} ★</h2>
              <p className="nb-block-sub">► {t('about.achievementsSubtitle')} ◄</p>
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
              {stats.map((stat, index) => {
                const trophyTints = ['nb-trophy--yellow', 'nb-trophy--pink', 'nb-trophy--blue', 'nb-trophy--green'];
                return (
                  <article key={index} className={`nb-trophy ${trophyTints[index % 4]}`}>
                    <span className="nb-trophy__ribbon">★ {String(index + 1).padStart(2, '0')} ★</span>
                    <div className="nb-trophy__icon">{stat.icon}</div>
                    <div className="nb-trophy__value">{stat.number}</div>
                    <div className="nb-trophy__label">{stat.label}</div>
                    <span className="nb-trophy__stamp">UNLOCKED</span>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* ============== CORE EXPERTISE / DIVISIONS ============== */}
        {isModern ? (
          <section className="mb-12 sm:mb-16 lg:mb-20">
            <div className="sf-block-header">
              <div className="sf-block-header__strip">
                <span className="sf-block-header__tag">DIVISIONS</span>
                <span className="sf-dossier__sep">▌</span>
                <span className="sf-block-header__id">{String(highlights.length).padStart(2, '0')}_ACTIVE</span>
                <span className="sf-dossier__sep">▌</span>
                <span className="sf-block-header__live">
                  <span className="sf-dossier__livedot"></span>
                  DEPLOYMENT_READY
                </span>
              </div>
              <h2 className="sf-block-header__h2">{t('about.expertiseTitle')}</h2>
              <p className="sf-block-header__sub">
                <span className="sf-bracket">[</span>{t('about.expertiseSubtitle')}<span className="sf-bracket">]</span>
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {highlights.map((highlight, index) => {
                const codes = ['AI', 'FSE', 'GDV', 'SEC'];
                const proficiency = [90, 92, 85, 95];
                return (
                  <article key={index} className="sf-division modern-card group">
                    <div className="sf-division__head">
                      <span className="sf-division__code">DIV_{String(index + 1).padStart(2, '0')}</span>
                      <span className="sf-division__sep">::</span>
                      <span className="sf-division__name">{codes[index]}</span>
                      <span className="sf-division__active">● ACTIVE</span>
                    </div>
                    <div className="sf-division__icon">{highlight.icon}</div>
                    <h3 className="sf-division__title">{highlight.title}</h3>
                    <p className="sf-division__desc">{highlight.description}</p>
                    <div className="sf-division__foot">
                      <div className="sf-division__bar-track">
                        <div className="sf-division__bar-fill" style={{ width: `${proficiency[index]}%` }}></div>
                      </div>
                      <div className="sf-division__bar-meta">
                        <span>PROFICIENCY</span>
                        <strong>{proficiency[index]}%</strong>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ) : (
          <section className="mb-12 sm:mb-16 lg:mb-20">
            <div className="text-center mt-8 mb-8">
              <h2 className="nb-block-h2">★ {t('about.expertiseTitle').toUpperCase()} ★</h2>
              <p className="nb-block-sub">► {t('about.expertiseSubtitle')} ◄</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
              {highlights.map((highlight, index) => {
                const tints = ['nb-crew--yellow', 'nb-crew--blue', 'nb-crew--green', 'nb-crew--pink'];
                const codes = ['AI', 'FSE', 'GDV', 'SEC'];
                return (
                  <article key={index} className={`nb-crew ${tints[index % tints.length]}`}>
                    <div className="nb-crew__banner">
                      <span className="nb-crew__banner-num">⚡ {String(index + 1).padStart(2, '0')}</span>
                      <span className="nb-crew__banner-code">DIV :: {codes[index]}</span>
                    </div>
                    <span className="nb-crew__stamp">★ ACTIVE ★</span>
                    <div className="nb-crew__icon">{highlight.icon}</div>
                    <h3 className="nb-crew__title">{highlight.title}</h3>
                    <p className="nb-crew__desc">{highlight.description}</p>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* ============== CTA / DEPLOYMENT REQUEST ============== */}
        {isModern ? (
          <section className="text-center">
            <article className="sf-cta">
              <div className="sf-cta__strip">
                <span className="sf-cta__tag">DEPLOYMENT_REQUEST</span>
                <span className="sf-dossier__sep">▌</span>
                <span className="sf-cta__priority">PRIORITY :: HIGH</span>
                <span className="sf-cta__live">
                  <span className="sf-dossier__livedot"></span>
                  AWAITING_BRIEF
                </span>
              </div>
              <h2 className="sf-cta__h2">
                <span className="bg-clip-text text-transparent">{t('about.ctaTitle')}</span>
              </h2>
              <p className="sf-cta__desc">
                <span className="sf-cta__prompt">&gt;_</span>
                {t('about.ctaDescription')}
              </p>
              <div className="sf-cta__actions">
                <a href="#contact" className="modern-button-primary px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 w-full sm:w-auto">
                  ▶ {t('about.ctaPrimary')}
                </a>
                <a href="#projects" className="modern-button-secondary px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 w-full sm:w-auto">
                  {t('about.ctaSecondary')} ▸
                </a>
              </div>
              <div className="sf-cta__foot">
                <span>STATUS :: <strong>OPEN</strong></span>
                <span className="sf-modules__counter-bar"></span>
                <span>RESPONSE :: <strong>~24H</strong></span>
                <span className="sf-modules__counter-bar"></span>
                <span>SECURE_RELAY :: <strong>ACTIVE</strong></span>
              </div>
            </article>
          </section>
        ) : (
          <section>
            <article className="nb-cta">
              <span className="nb-stamp nb-stamp--certified" style={{ top: '-18px', right: '40px' }}>★ READY ★</span>
              <span className="nb-cta__corner">!</span>
              <h2 className="nb-cta__h2">★ {t('about.ctaTitle').toUpperCase()} ★</h2>
              <p className="nb-cta__desc">{t('about.ctaDescription')}</p>
              <div className="nb-cta__actions">
                <a href="#contact" className={ctaButtonStyles(true)}>▶ {t('about.ctaPrimary')}</a>
                <a href="#projects" className={ctaButtonStyles(false)}>{t('about.ctaSecondary')} ▸</a>
              </div>
              <div className="nb-cta__meta">
                <span>★ STATUS: OPEN</span>
                <span>★ RESPONSE: ~24H</span>
                <span>★ WORLDWIDE</span>
              </div>
            </article>
          </section>
        )}
      </div>
    </section>
  );
};

export default About;
